import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { OrganizationsTab } from "./Organizations";
import { RolesTab } from "./Roles";
import { UsersTab } from "./Users";
import BusinessIcon from "@mui/icons-material/Business";
import SecurityIcon from "@mui/icons-material/Security";
import { organizationsApi } from "../../providers/env";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const RolesAndPermissionsPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [userRole, setUserRole] = useState<string>("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<string[]>([]);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [organizationName, setOrganizationName] = useState<string>("");
  const [isOrgContext, setIsOrgContext] = useState(false); // Is viewing in org context (not super admin global view)

  useEffect(() => {
    loadUserInfo();
  }, []);

  const refreshOrganizationNames = async () => {
    try {
      const response = await organizationsApi.getOrganizations();
      const orgs = response.data || [];
      const permissions = localStorage.getItem("permissions");
      const userInfo = permissions ? JSON.parse(permissions) : {};
      const isSuperUser = userInfo.is_superuser || false;
      const isOnSuperAdminRoute = window.location.pathname.startsWith('/super-admin');

      if (!isSuperUser || (isSuperUser && !isOnSuperAdminRoute && userInfo.organization_id)) {
        const userOrg = orgs.find((org: any) => org.id === userInfo.organization_id);
        setOrganizations(userOrg ? [userOrg.name] : []);
      } else {
        setOrganizations(orgs.map((org: any) => org.name));
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const loadUserInfo = async () => {
    try {
      const permissions = localStorage.getItem("permissions");
      if (permissions) {
        const userInfo = JSON.parse(permissions);
        const isSuperUser = userInfo.is_superuser || false;
        setIsSuperAdmin(isSuperUser);

        if (isSuperUser) {
          setUserRole("Super Admin");
          // Super admin viewing from /super-admin route sees all orgs
          // Super admin on /roles-and-permissions would be org-scoped (if they somehow accessed it)
          const isOnSuperAdminRoute = window.location.pathname.startsWith('/super-admin');
          setIsOrgContext(!isOnSuperAdminRoute);

          if (!isOnSuperAdminRoute && userInfo.organization_id) {
            // Super admin but viewing org-specific page
            setOrganizationId(userInfo.organization_id);
          }
        } else {
          // Org Admin or Sub Admin
          setIsOrgContext(true);
          if (userInfo.role) {
            setUserRole(userInfo.role.name || "User");
          }

          // Get organization from user data
          if (userInfo.organization_id) {
            setOrganizationId(userInfo.organization_id);
          }
          if (userInfo.organization) {
            setOrganizationName(userInfo.organization.name || userInfo.organization);
          }
        }

        await refreshOrganizationNames();
      }
    } catch (error) {
      console.error("Error loading user info", error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box sx={{ pl: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontWeight: 600,
                color: "#1a1a1a",
                mb: 1
              }}
            >
              Roles & Permissions
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: "0.875rem" }}
            >
              Manage roles, permissions, and user access across your organization
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <SecurityIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                Your Role
              </Typography>
            </Box>
            <Chip 
              label={userRole} 
              size="small" 
              sx={{ 
                backgroundColor: "#9c27b0",
                color: "white",
                fontWeight: 500,
                height: 24,
                "& .MuiChip-label": {
                  px: 1.5,
                  fontSize: "0.75rem"
                }
              }} 
            />
          </Box>
        </Box>

        {organizations.length > 0 && (
          <Card 
            sx={{ 
              mt: 2,
              mb: 2,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              borderRadius: 2
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  fontSize: "0.75rem",
                  fontWeight: 500
                }}
              >
                <BusinessIcon fontSize="small" sx={{ fontSize: 16 }} />
                {isOrgContext ? "Your Organization" : "Your Organizations"}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {organizations.map((org, index) => (
                  <Chip
                    key={index}
                    label={org}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: "#e0e0e0",
                      color: "text.primary",
                      fontWeight: 400,
                      "&:hover": {
                        backgroundColor: "#f5f5f5"
                      }
                    }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      <Paper 
        sx={{ 
          mb: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: 2
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="roles and permissions tabs"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: 48,
            },
            "& .Mui-selected": {
              color: "#1976d2",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#1976d2",
            }
          }}
        >
          <Tab label="Roles" />
          <Tab label="Users" />
          {isSuperAdmin && !isOrgContext && <Tab label="Organizations" />}
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        <RolesTab organizationId={organizationId} isOrgContext={isOrgContext} />
      </TabPanel>
      <TabPanel value={currentTab} index={1}>
        <UsersTab organizationId={organizationId} isOrgContext={isOrgContext} />
      </TabPanel>
      {isSuperAdmin && !isOrgContext && (
        <TabPanel value={currentTab} index={2}>
          <OrganizationsTab onOrgUpdated={refreshOrganizationNames} />
        </TabPanel>
      )}
    </Box>
  );
};


export default RolesAndPermissionsPage;